import { useQuery } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import {
  DASHBOARD_RANGE_LABELS,
  DASHBOARD_RANGES,
  PAYMENT_CHANNELS,
  PAYMENT_METHOD_LABELS,
  type DashboardKpis,
  type DashboardRange,
  type DashboardSummary,
  type NotificationDTO,
} from '@bifeai/shared';
import { BarChart, Card, CardBody, CardHeader, EmptyState, Input, Kpi, Select, Skeleton } from '@bifeai/ui';
import { BellOff, Boxes, PackageMinus, Receipt, ShoppingBasket, ShoppingCart, TrendingUp, Weight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { api } from '../lib/api';
import { capitalize, firstName, formatDate, formatKilos, formatLongDate, formatMoney, timeAgo } from '../lib/format';
import { formatQty, todayIso } from '../lib/units';
import { useSession } from '../lib/session';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 6) return 'Buenas noches';
  if (h < 13) return 'Buen día';
  if (h < 20) return 'Buenas tardes';
  return 'Buenas noches';
}

const PERIOD_KEY = 'bifeai.dashboard.period';
export interface Period {
  range: DashboardRange;
  from: string;
  to: string;
}
function loadPeriod(): Period {
  try {
    const p = JSON.parse(localStorage.getItem(PERIOD_KEY) ?? 'null') as Period | null;
    if (p && (DASHBOARD_RANGES as readonly string[]).includes(p.range)) return p;
  } catch {
    /* sin almacenamiento: se usa el valor por defecto */
  }
  return { range: '30d', from: todayIso(), to: todayIso() };
}

/** Variación contra el período anterior: "▲ 12 % vs. período anterior". */
export function Delta({ now, before, money = false }: { now: number; before: number; money?: boolean }) {
  if (before === 0 && now === 0) return <span>Sin movimiento en el período</span>;
  if (before === 0) return <span>Sin datos del período anterior</span>;
  const pct = Math.round(((now - before) / before) * 100);
  const tone = pct > 0 ? 'bf-text-success' : pct < 0 ? 'bf-text-danger' : 'bf-text-muted';
  return (
    <span>
      <span className={tone} style={{ fontWeight: 700 }}>
        {pct > 0 ? '▲' : pct < 0 ? '▼' : '='} {Math.abs(pct)} %
      </span>{' '}
      vs. anterior{money ? ` (${formatMoney(before)})` : ''}
    </span>
  );
}

export function PeriodPicker({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  return (
    <div className="bf-period">
      <Select aria-label="Período" value={value.range} onChange={(e) => onChange({ ...value, range: e.target.value as DashboardRange })}>
        {DASHBOARD_RANGES.map((r) => (
          <option key={r} value={r}>
            {r === 'today' || r === 'custom' ? DASHBOARD_RANGE_LABELS[r] : `Últimos ${DASHBOARD_RANGE_LABELS[r]}`}
          </option>
        ))}
      </Select>
      {value.range === 'custom' && (
        <>
          <Input
            type="date"
            aria-label="Desde"
            value={value.from}
            max={value.to}
            onChange={(e) => e.target.value && onChange({ ...value, from: e.target.value })}
          />
          <span className="bf-cell-sub">a</span>
          <Input
            type="date"
            aria-label="Hasta"
            value={value.to}
            min={value.from}
            max={todayIso()}
            onChange={(e) => e.target.value && onChange({ ...value, to: e.target.value })}
          />
        </>
      )}
    </div>
  );
}

/**
 * Inicio: el dashboard. Quien no vende en el mostrador ni ve el dashboard arranca en lo suyo: el vendedor
 * mayorista en sus pedidos y el repartidor en su reparto.
 */
export function DashboardPage() {
  const { can } = useSession();
  if (!can('dashboard.view') && !can('pos.sell')) {
    if (can('wholesale.manage') || can('wholesale.sell')) return <Navigate to="/mayorista" replace />;
    if (can('wholesale.deliver')) return <Navigate to="/reparto" replace />;
  }
  return <DashboardHome />;
}

function DashboardHome() {
  const { me, can, multiBranch, selectedBranchId } = useSession();
  const [period, setPeriod] = useState<Period>(loadPeriod);
  useEffect(() => {
    try {
      localStorage.setItem(PERIOD_KEY, JSON.stringify(period));
    } catch {
      /* ignorar */
    }
  }, [period]);
  // Con franquicias, la central elige qué mira: sus locales, las franquicias o toda la red.
  const [view, setView] = useState<'own' | 'franchises' | 'network' | null>(null);
  const qs = new URLSearchParams({ range: period.range });
  if (view) qs.set('view', view);
  if (period.range === 'custom') {
    qs.set('from', period.from);
    qs.set('to', period.to);
  }
  const summary = useQuery({
    queryKey: ['dashboard', 'summary', selectedBranchId, qs.toString()],
    queryFn: () => api.get<DashboardSummary>(`/dashboard/summary?${qs.toString()}`),
    enabled: can('dashboard.view'),
    placeholderData: keepPreviousData,
  });
  const recent = useQuery({
    queryKey: ['notifications', 'list', 5],
    queryFn: () => api.get<NotificationDTO[]>('/notifications?limit=5'),
  });

  const branchName = me.branches.find((b) => b.id === selectedBranchId)?.name;
  const data = summary.data;
  const VIEW_TEXT = {
    own: 'Mirando tus locales propios.',
    franchises: 'Mirando todas las franquicias juntas (ventas de cada franquiciado).',
    network: 'Mirando toda la red: tus locales y las franquicias juntos.',
  } as const;
  const scopeText = branchName
    ? `Mirando ${branchName}.`
    : data?.view
      ? VIEW_TEXT[data.view]
      : multiBranch
        ? 'Mirando todas las sucursales juntas.'
        : '';
  const k = data?.kpis;
  const prev: DashboardKpis | undefined = data?.previous;
  const trend = data?.trend.map((t) => t.salesCents);
  const countTrend = data?.trend.map((t) => t.salesCount);
  const periodText = data
    ? data.period.from === data.period.to
      ? formatDate(data.period.from)
      : `${formatDate(data.period.from)} al ${formatDate(data.period.to)}`
    : '';

  return (
    <>
      <div className="bf-page-head">
        <div>
          <h2 className="bf-page-title">¡Hola, {firstName(me.user.name)}!</h2>
          <p className="bf-page-sub">
            {greeting()}. Hoy es {formatLongDate(new Date())} y así viene {me.tenant.name}. {scopeText}
          </p>
        </div>
        {can('dashboard.view') ? (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
            {data?.view && (
              <div className="bf-view-switch" role="group" aria-label="Qué mirar">
                {(
                  [
                    ['own', 'Locales propios'],
                    ['franchises', 'Franquicias'],
                    ['network', 'Toda la red'],
                  ] as const
                ).map(([v, label]) => (
                  <button key={v} type="button" aria-pressed={data.view === v} onClick={() => setView(v)}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            <PeriodPicker value={period} onChange={setPeriod} />
          </div>
        ) : (
          can('pos.sell') && (
            <Link to="/venta" className="bf-btn bf-btn--primary">
              <ShoppingCart /> Ir a vender
            </Link>
          )
        )}
      </div>

      {!can('dashboard.view') ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={<ShoppingBasket />}
              title={can('pos.sell') ? 'Listo para atender' : 'Bienvenido'}
              text={
                can('pos.sell')
                  ? 'Desde el punto de venta cobrás, y en Caja ves el efectivo de tu turno.'
                  : 'En el menú de la izquierda tenés las secciones que te habilitó el dueño.'
              }
              action={
                can('pos.sell') ? (
                  <Link to="/venta" className="bf-btn bf-btn--primary">
                    Abrir el punto de venta
                  </Link>
                ) : undefined
              }
            />
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardHeader
            title="Vista general"
            subtitle={periodText ? `Del ${periodText}, comparado con el período anterior de igual duración.` : 'Indicadores del período'}
          />
          <CardBody>
            {summary.isPending || !k || !prev ? (
              <div className="bf-kpi-grid">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} style={{ height: 116 }} />
                ))}
              </div>
            ) : (
              <div className="bf-kpi-grid" style={{ opacity: summary.isPlaceholderData ? 0.6 : 1 }}>
                <Kpi
                  label="Ventas"
                  icon={<Receipt />}
                  value={formatMoney(k.salesCents)}
                  delta={<Delta now={k.salesCents} before={prev.salesCents} money />}
                  deltaMuted
                  trend={trend}
                />
                <Kpi
                  label="Tickets"
                  tone="green"
                  icon={<ShoppingBasket />}
                  value={k.salesCount}
                  delta={<Delta now={k.salesCount} before={prev.salesCount} />}
                  deltaMuted
                  trend={countTrend}
                />
                <Kpi
                  label="Kilos vendidos"
                  tone="amber"
                  icon={<Weight />}
                  value={formatKilos(k.kilosSoldGrams)}
                  delta={<Delta now={k.kilosSoldGrams} before={prev.kilosSoldGrams} />}
                  deltaMuted
                />
                {k.grossMarginCents !== null ? (
                  <Kpi
                    label="Ganancia bruta"
                    tone="red"
                    icon={<TrendingUp />}
                    value={formatMoney(k.grossMarginCents)}
                    delta={
                      k.salesCents > 0 ? (
                        <span>
                          {Math.round((k.grossMarginCents / k.salesCents) * 100)} % de margen · ticket prom. {formatMoney(k.averageTicketCents)}
                        </span>
                      ) : (
                        'Ventas menos costo de la mercadería'
                      )
                    }
                    deltaMuted
                  />
                ) : (
                  <Kpi
                    label="Ticket promedio"
                    tone="red"
                    icon={<Receipt />}
                    value={formatMoney(k.averageTicketCents)}
                    delta={<Delta now={k.averageTicketCents} before={prev.averageTicketCents} />}
                    deltaMuted
                  />
                )}
              </div>
            )}
            {data && (data.waste.grams > 0 || data.waste.tachoIncomeCents > 0) && (
              <p className="bf-help" style={{ marginTop: 14 }}>
                <PackageMinus aria-hidden />
                <span>
                  Merma del período: {formatKilos(data.waste.grams)}
                  {data.waste.costCents !== null && <> (costaba {formatMoney(data.waste.costCents)})</>}
                  {data.waste.tachoIncomeCents > 0 && <> · el tacho devolvió {formatMoney(data.waste.tachoIncomeCents)}</>}
                  {data.waste.lossCents !== null && k && k.salesCents > 0 && (
                    <>
                      {' '}
                      · pérdida real <strong>{formatMoney(Math.max(0, data.waste.lossCents))}</strong> (
                      {((Math.max(0, data.waste.lossCents) / k.salesCents) * 100).toFixed(1)} % de lo vendido)
                    </>
                  )}
                  . <Link to="/mermas">Ver mermas y tacho</Link>
                </span>
              </p>
            )}
            {k && k.expiringLots > 0 && (
              <p className="bf-help" style={{ marginTop: 14 }}>
                <Boxes aria-hidden />
                <span>
                  {k.expiringLots} {k.expiringLots === 1 ? 'lote vence' : 'lotes vencen'} en los próximos 2 días. <Link to="/stock">Ver stock</Link>
                </span>
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {can('dashboard.view') && data && data.kpis.salesCount > 0 && (
        <>
          <PaymentChannels payments={data.payments} />
          <Card>
            <CardHeader
              title="Evolución de las ventas"
              subtitle={data.period.granularity === 'day' ? 'Por día' : data.period.granularity === 'week' ? 'Por semana' : 'Por mes'}
            />
            <CardBody>
              <TrendChart points={data.trend} granularity={data.period.granularity} />
            </CardBody>
          </Card>
          <div className="bf-dash-grid">
            <Card>
              <CardHeader title="Lo más vendido" subtitle="Por importe en el período" />
              <CardBody flush>
                <table className="bf-table">
                  <tbody>
                    {data.topProducts.map((p, i) => (
                      <tr key={p.productId}>
                        <td className="bf-cell-sub" style={{ width: 28 }}>
                          {i + 1}
                        </td>
                        <td className="bf-cell-main">{p.name}</td>
                        <td className="bf-num bf-cell-sub">{formatQty(p.unitType, p.qty)}</td>
                        <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatMoney(p.salesCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </div>
        </>
      )}

      {data?.receivables && data.receivables.debtors > 0 && (
        <Card>
          <CardHeader
            title="Cuenta corriente"
            subtitle={`${formatMoney(data.receivables.totalCents)} para cobrar entre ${data.receivables.debtors} ${data.receivables.debtors === 1 ? 'cliente' : 'clientes'}${data.receivables.overdue ? ` · ${data.receivables.overdue} con deuda atrasada` : ''}`}
            actions={<Link to="/clientes">Ver clientes</Link>}
          />
          <CardBody flush>
            <table className="bf-table">
              <tbody>
                {data.receivables.top.map((c) => (
                  <tr key={c.id}>
                    <td className="bf-cell-main">
                      <Link to={`/clientes/${c.id}`}>{c.name}</Link> {c.overdue && <span className="bf-text-warning bf-cell-sub">· atrasada</span>}
                    </td>
                    <td className="bf-cell-sub">{c.lastMovementAt ? capitalize(timeAgo(c.lastMovementAt)) : ''}</td>
                    <td className="bf-num bf-text-danger" style={{ textAlign: 'right', fontWeight: 600 }}>
                      {formatMoney(c.balanceCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      <div>
        <Card>
          <CardHeader title="Últimas alertas" subtitle="Vencimientos, stock y pagos" actions={<Link to="/alertas">Ver todas</Link>} />
          <CardBody flush>
            {recent.data?.length === 0 && <EmptyState icon={<BellOff />} title="Todo en orden" text="No hay alertas por ahora." />}
            {recent.data?.map((n) => (
              <div key={n.id} className="bf-notif" data-read={n.read} style={{ cursor: 'default' }}>
                <span className={`bf-notif-dot bf-notif-dot--${n.severity}`} aria-hidden />
                <div>
                  <p className="bf-notif-title">{n.title}</p>
                  {n.body && <p className="bf-notif-body">{n.body}</p>}
                  <p className="bf-notif-time">{capitalize(timeAgo(n.createdAt))}</p>
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      </div>

      {multiBranch && !selectedBranchId && data && (
        <Card>
          <CardHeader title="Ventas por sucursal" subtitle="Comparación del período, incluida la casa central" />
          <CardBody>
            <Bars
              rows={data.branches.map((b) => ({
                key: b.id,
                label: b.name,
                value: b.salesCents,
                extra: `${b.salesCount} tickets`,
              }))}
              total={data.branches.reduce((a, b) => a + b.salesCents, 0)}
            />
          </CardBody>
        </Card>
      )}
    </>
  );
}

function Bars({ rows, total }: { rows: { key: string; label: string; value: number; extra?: string }[]; total: number }) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  if (rows.length === 0) return <p className="bf-cell-sub">Sin datos en el período.</p>;
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {rows.map((r) => (
        <div key={r.key} className="bf-bar-row">
          <span style={{ fontWeight: 600 }}>{r.label}</span>
          <span className="bf-bar">
            <span style={{ width: `${(r.value / max) * 100}%` }} />
          </span>
          <span className="bf-num" style={{ textAlign: 'right', color: 'var(--bf-muted)' }}>
            {formatMoney(r.value)}
            <small style={{ display: 'block' }}>
              {total > 0 ? `${Math.round((r.value / total) * 100)} %` : ''}
              {r.extra ? ` · ${r.extra}` : ''}
            </small>
          </span>
        </div>
      ))}
    </div>
  );
}

const shortDay = new Intl.DateTimeFormat('es-AR', {
  day: '2-digit',
  month: '2-digit',
});
const monthFmt = new Intl.DateTimeFormat('es-AR', {
  month: 'short',
  year: '2-digit',
});

/** Gráfico de barras simple en SVG: no hace falta una librería de gráficos. */
export function TrendChart({ points, granularity }: { points: DashboardSummary['trend']; granularity: 'day' | 'week' | 'month' }) {
  const label = (iso: string) => {
    const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
    return granularity === 'month' ? monthFmt.format(d) : shortDay.format(d);
  };
  return (
    <BarChart
      ariaLabel="Ventas por período"
      data={points.map((p) => ({ key: p.date, label: label(p.date), value: p.salesCents }))}
      tip={(i) => {
        const p = points[i]!;
        return {
          title: granularity === 'week' ? `Semana del ${label(p.date)}` : label(p.date),
          lines: [formatMoney(p.salesCents), `${p.salesCount} tickets`],
        };
      }}
    />
  );
}

const CHANNEL_COLORS: Record<string, string> = {
  CASH: 'var(--bf-success)',
  CARD: 'var(--bf-celeste-strong)',
  MERCADOPAGO: '#009ee3',
  CUENTA_DNI: '#7c3aed',
  TRANSFER: 'var(--bf-warning)',
  QR: '#db2777',
  ACCOUNT: 'var(--bf-danger)',
  OTHER: 'var(--bf-faint)',
};

/** ¿Por dónde entra la plata? Un recuadro por canal: importe, % del total, cantidad de cobros y comparación. */
function PaymentChannels({ payments }: { payments: DashboardSummary['payments'] }) {
  const channels = PAYMENT_CHANNELS.map((c) => {
    const rows = payments.filter((p) => (c.methods as readonly string[]).includes(p.method));
    return {
      ...c,
      amount: rows.reduce((a, r) => a + r.amountCents, 0),
      prev: rows.reduce((a, r) => a + r.prevAmountCents, 0),
      count: rows.reduce((a, r) => a + r.count, 0),
      detail: rows.length > 1 ? rows.map((r) => `${PAYMENT_METHOD_LABELS[r.method]} ${formatMoney(r.amountCents)}`).join(' · ') : null,
    };
  })
    .filter((c) => c.amount > 0)
    .sort((a, b) => b.amount - a.amount);
  const total = channels.reduce((a, c) => a + c.amount, 0);
  if (total === 0) return null;
  const top = channels[0]!;
  return (
    <Card>
      <CardHeader
        title="¿Cómo te pagan?"
        subtitle={`Por dónde entró la plata en el período. El que más se usa: ${top.label} (${Math.round((top.amount / total) * 100)} %).`}
      />
      <CardBody>
        <div className="bf-share-bar" role="img" aria-label="Reparto de los cobros por medio de pago">
          {channels.map((c) => (
            <span
              key={c.key}
              style={{ width: `${(c.amount / total) * 100}%`, background: CHANNEL_COLORS[c.key] }}
              title={`${c.label}: ${Math.round((c.amount / total) * 100)} %`}
            />
          ))}
        </div>
        <div className="bf-channel-grid">
          {channels.map((c) => (
            <div key={c.key} className="bf-channel">
              <span className="bf-channel-name">
                <i style={{ background: CHANNEL_COLORS[c.key] }} aria-hidden /> {c.label}
              </span>
              <strong className="bf-num">{formatMoney(c.amount)}</strong>
              <span className="bf-channel-meta">
                {Math.round((c.amount / total) * 100)} % · {c.count} {c.count === 1 ? 'cobro' : 'cobros'}
              </span>
              {c.detail && <span className="bf-channel-meta">{c.detail}</span>}
              <span className="bf-channel-meta">
                <Delta now={c.amount} before={c.prev} money />
              </span>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}
